import {
  Component,
  OnInit,
  TemplateRef,
  viewChild,
  ChangeDetectionStrategy,
  input,
  model,
  effect,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormsModule,
} from '@angular/forms';

// dayjs plugins
import dayjs from 'dayjs';
import isoWeeksInYear from 'dayjs/plugin/isoWeeksInYear';
import isLeapYear from 'dayjs/plugin/isLeapYear';
import isoWeek from 'dayjs/plugin/isoWeek';
dayjs.extend(isoWeek);
dayjs.extend(isoWeeksInYear);
dayjs.extend(isLeapYear); // dependency for isoWeeksInYear

// components
import { Popover } from 'primeng/popover';

// types
import { weekString } from '@app/core/types/week-string.type';
import { NgTemplateOutlet } from '@angular/common';
import { Bind } from 'primeng/bind';
import { NgSelectComponent } from '@ng-select/ng-select';
import { Button } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { AngularSvgIconModule } from 'angular-svg-icon';

export interface IWeekRange {
  startWeek: weekString;
  endWeek: weekString;
}

@Component({
  selector: 'ag1-week-range',
  templateUrl: './ag1-week-range.component.html',
  providers: [
    {
      // add component as a value accessor to angular forms
      // this allows the component to be used as a form control
      // and to be updated by the form control
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: Ag1WeekRangeComponent,
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    NgTemplateOutlet,
    Bind,
    Popover,
    NgSelectComponent,
    FormsModule,
    Button,
    Tooltip,
    AngularSvgIconModule,
  ],
})
export class Ag1WeekRangeComponent implements OnInit, ControlValueAccessor {
  // custom template
  /** Pass in a template to customize the trigger button */
  readonly customTriggerTemplateRef = input<TemplateRef<any>>(undefined);

  /** Pass in a template to customize the displayed value */
  readonly customValueTemplateRef = input<TemplateRef<any>>(undefined);

  /** Indicates if the week range has an error/invalid state */
  readonly hasError = input<boolean>(false);

  // date range (Input + Change pair — parents may use [x]/(xChange) or [(x)])
  readonly startWeek = model<weekString>(undefined);
  readonly endWeek = model<weekString>(undefined);

  // display
  // configures whether the user can select a single week or a range of weeks
  readonly selectionType = input<'range' | 'single'>('range');
  readonly separator = input<string>('-');

  // use ViewChild to access the menu component
  // in order to close the menu from outside the component
  readonly weekRangePickerPopover = viewChild<Popover>('weekRangePicker');

  placeholder: string;
  weekSelection: 'initialRange' | 'alterRange' = 'initialRange';

  // selections
  selectedStartWeek: weekString;
  selectedEndWeek: weekString;

  currentWeekString: weekString;
  calendarYear: number;

  weeks: number[] = [];
  years: number[];

  weekFormat: string = 'GGGG[-W]WW';

  readonly RANGE_SELECTION = 'range' as const;
  readonly SINGLE_SELECTION = 'single' as const;

  private previousSelectionType: 'range' | 'single' | undefined;

  constructor() {
    effect(() => {
      const selectionType: 'range' | 'single' = this.selectionType();
      if (
        this.previousSelectionType === this.RANGE_SELECTION &&
        selectionType === this.SINGLE_SELECTION
      ) {
        this.endWeek.set(this.startWeek());
        this.applyWeekRange();
      }
      this.previousSelectionType = selectionType;
    });

    effect(() => {
      const startWeek: weekString = this.startWeek();
      const endWeek: weekString = this.endWeek();

      if (startWeek && endWeek) {
        this.selectedStartWeek = startWeek;
        this.selectedEndWeek = endWeek;
      }
    });
  }

  ngOnInit(): void {
    this.currentWeekString = this.getCurrentWeek();
    this.calendarYear = dayjs(
      this.currentWeekString,
      this.weekFormat,
    ).isoWeekYear();
    this.placeholder = this._weekPlaceholder();
    this.years = this.generateYearOptions(this.calendarYear);
    this.generateCalendarStructure();
  }

  private _weekPlaceholder(): string {
    return this.isRangeSelection ? 'Select a week range' : 'Select a week';
  }

  getCurrentWeek(): weekString {
    return `${dayjs().isoWeekYear()}-W${dayjs()
      .isoWeek()
      .toString()
      .padStart(2, '0')}` as weekString; // eg 2025-W01
  }

  getWeeksInYear(year: number): number {
    // choose middle of year to avoid issues with leap years and ISO week year boundaries
    return dayjs(`${year}-06-01`).isoWeeksInYear();
  }

  generateYearOptions(calendarYear: number): number[] {
    const yearPadding: number = 10;
    const years: number[] = [];
    for (let i = yearPadding; i >= 0; i--) {
      years.push(calendarYear - i);
    }

    for (let i = 1; i <= yearPadding; i++) {
      years.push(calendarYear + i);
    }
    return years;
  }

  changeCurrentYear(calendarYear: number): void {
    this.calendarYear = calendarYear;
    this.generateCalendarStructure();
  }

  generateCalendarStructure(): void {
    this.weeks = [];

    // gets the number of weeks in the current year
    const weeksCount: number = this.getWeeksInYear(this.calendarYear);

    for (let i = 1; i <= weeksCount; i++) {
      this.weeks.push(i);
    }
  }

  generatePreviousYearCalendar(): void {
    this.calendarYear--;
    this.generateCalendarStructure();
  }

  generateNextYearCalendar(): void {
    this.calendarYear++;
    this.generateCalendarStructure();
  }

  getWeekString(week: number, year: number): weekString {
    // zero-pad the week number if it is less than 10
    const weekString: string =
      week < 10 ? `${year}-W0${week}` : `${year}-W${week}`;

    return weekString as weekString;
  }

  getWeekStyle(week: number): string {
    const weekString: weekString = this.getWeekString(week, this.calendarYear);

    const classes: string[] = [
      'p-1',
      'aspect-square',
      'text-center',
      'select-none',
      'cursor-pointer',
      'outline-none',
      'focus-visible:ring-2',
      'focus-visible:ring-offset-2',
      'focus-visible:ring-offset-white',
      'focus-visible:ring-french-blue-500',
    ];

    // most specific first
    // style initial selection (i.e both start and end weeks are set the same)
    if (
      this.compareWeeks(this.startWeek(), weekString) &&
      this.compareWeeks(this.endWeek(), weekString)
    ) {
      classes.push(
        'bg-french-blue-500',
        'text-white',
        'rounded',
        'hover:bg-french-blue-600',
      );
    } else if (this.styleSelectedEndWeek(weekString)) {
      // style end week
      classes.push(
        'bg-french-blue-500',
        'text-white',
        'rounded-r',
        'hover:bg-french-blue-600',
      );
    } else if (this.styleSelectedStartWeek(weekString)) {
      // style start week
      classes.push(
        'bg-french-blue-500',
        'text-white',
        'rounded-l',
        'hover:bg-french-blue-600',
      );
    } else if (this.styleSelectedRange(weekString)) {
      // style weeks in range
      classes.push('bg-french-blue-100', 'hover:bg-french-blue-200');
    } else if (this.styleCurrentWeek(weekString)) {
      // style current week
      classes.push(
        'rounded-full bg-white text-french-blue-500 border border-french-blue-500 hover:bg-slate-100',
      );
    } else {
      // style other weeks
      classes.push('rounded hover:bg-slate-100');
    }

    return classes.join(' ');
  }

  compareWeeks(week1String: weekString, week2String: weekString): boolean {
    if (!week1String || !week2String) return false;

    // no need to use dayjs as we are comparing strings
    return week1String === week2String;
  }

  styleCurrentWeek(weekString: weekString): boolean {
    return this.compareWeeks(weekString, this.currentWeekString);
  }

  styleSelectedStartWeek(weekString: weekString): boolean {
    if (!this.startWeek()) return false;

    return this.compareWeeks(weekString, this.startWeek());
  }

  styleSelectedEndWeek(weekString: weekString): boolean {
    if (!this.endWeek()) return false;

    return this.compareWeeks(weekString, this.endWeek());
  }

  styleSelectedRange(weekString: weekString): boolean {
    if (!this.startWeek() || !this.endWeek()) return false;

    const { year: year, week: week } = this.splitWeekString(weekString);
    const { year: startYear, week: startWeek } = this.splitWeekString(
      this.startWeek(),
    );
    const { year: endYear, week: endWeek } = this.splitWeekString(
      this.endWeek(),
    );

    // eg. Test case 2025-W52 to 2026-W53
    const isAfterStartWeek: boolean =
      year > startYear || (year === startYear && week > startWeek);
    const isBeforeEndWeek: boolean =
      year < endYear || (year === endYear && week < endWeek);

    return isAfterStartWeek && isBeforeEndWeek;
  }

  selectWeek(week: { week: number; year: number }): void {
    const selectedWeekString: weekString = this.getWeekString(
      week.week,
      week.year,
    );

    if (this.isSingleSelection) {
      this.setStartWeek(selectedWeekString);
      this.setEndWeek(selectedWeekString);
      this.weekSelection = 'initialRange';

      this.applyWeekRange();
      return;
    }

    if (this.weekSelection === 'initialRange') {
      // initial selection
      this.setStartWeek(selectedWeekString);
      this.setEndWeek(selectedWeekString);
      this.weekSelection = 'alterRange';

      // update the UI
      this.displayWeekRange(this.startWeek(), this.endWeek());
    } else {
      // second selection alters the range
      // but because a user can select weeks in any orders

      // Only use the initial selection and the new selection
      const weeks: weekString[] = [this.startWeek(), selectedWeekString];

      this.orderAndSetWeeks(weeks);
      this.weekSelection = 'initialRange';

      this.applyWeekRange();
    }
  }

  setStartWeek(weekString: weekString) {
    this.startWeek.set(weekString);
  }

  setEndWeek(weekString: weekString) {
    this.endWeek.set(weekString);
  }

  splitWeekString(weekString: weekString): { year: number; week: number } {
    const year: number = parseInt(weekString.split('-W')[0]);
    const week: number = parseInt(weekString.split('-W')[1]);
    return { year, week };
  }

  orderAndSetWeeks(weekStrings: weekString[]): void {
    weekStrings.sort((a: weekString, b: weekString) => {
      const { year: yearA, week: weekA } = this.splitWeekString(a);
      const { year: yearB, week: weekB } = this.splitWeekString(b);

      // Compare years first
      if (yearA !== yearB) {
        return yearA - yearB;
      }

      // If years are the same, compare week numbers
      return weekA - weekB;
    });

    this.setStartWeek(weekStrings[0]);
    this.setEndWeek(weekStrings[weekStrings.length - 1]);
  }

  displayWeekRange(
    startWeekString: weekString,
    endWeekString: weekString,
  ): void {
    if (!startWeekString || !endWeekString) {
      return;
    }

    this.selectedStartWeek = startWeekString;
    this.selectedEndWeek = endWeekString;
  }

  clearWeeks(): void {
    // reset values
    this.startWeek.set(null);
    this.endWeek.set(null);

    // reset selections
    this.weekSelection = 'initialRange';

    this.applyWeekRange();
  }

  closeMenu(): void {
    this.weekRangePickerPopover()?.hide();
  }

  handleTriggerClick(event: Event): void {
    event.stopImmediatePropagation();

    if (!(this.disabled || this.readonly())) {
      this.weekRangePickerPopover()?.toggle(event);
    }
  }

  applyWeekRange(): void {
    this.markAsTouched();

    if (!this.disabled) {
      // update form control
      this.onChange(this.getValue());

      // update the UI
      this.displayWeekRange(this.startWeek(), this.endWeek());
    }

    this.closeMenu();
  }

  getValue(): { startWeek: weekString; endWeek: weekString } | null {
    if (!this.startWeek() || !this.endWeek()) {
      return null;
    }

    return {
      startWeek: this.startWeek(),
      endWeek: this.endWeek(),
    };
  }

  get isRangeSelection(): boolean {
    return this.selectionType() === this.RANGE_SELECTION;
  }

  get isSingleSelection(): boolean {
    return this.selectionType() === this.SINGLE_SELECTION;
  }

  // required by ControlValueAccessor

  // ControlValueAccessor methods
  // used to update the form control
  onChange: (value: IWeekRange | null) => void = (value) => {};

  // used to inform the form control that the user has interacted with the component
  onTouched: () => void = () => {};

  // used to set the value using the form control
  writeValue(value: IWeekRange | null): void {
    if (value) {
      if (this.isRangeSelection) {
        this.startWeek.set(value.startWeek);
        this.endWeek.set(value.endWeek);
      } else {
        this.startWeek.set(value.startWeek);
        this.endWeek.set(value.startWeek);
      }

      // update the UI
      this.displayWeekRange(this.startWeek(), this.endWeek());
    } else {
      this.clearInput();
    }
  }

  // used to register the onChange function
  registerOnChange(onChange: (value: IWeekRange | null) => void): void {
    this.onChange = onChange;
  }

  // used to register the onTouched function
  registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  // used to set the disabled state using the form control
  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  clearInput(): void {
    this.startWeek.set(null);
    this.endWeek.set(null);
    this.selectedStartWeek = null;
    this.selectedEndWeek = null;
  }

  // used to inform the form control that the user has interacted with the component
  markAsTouched(): void {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  // ControlValueAccessor properties
  // used to track the state of the form control
  touched: boolean = false;
  disabled: boolean = false;
  readonly readonly = input<boolean>(false);

  get hasValue(): boolean {
    return !!this.startWeek() && !!this.endWeek();
  }
}
